<template>
  <v-card
    elevation="3"
    class="document-card"
    :class="{ 'document-card--aja': isThemeAja }"
    @click="previewDocument"
  >
    <!-- Document Icon and Type Badge -->
    <div class="document-card__header">
      <div class="document-card__icon-wrapper">
        <v-icon size="48" :color="iconColor">{{ documentIcon }}</v-icon>
      </div>
      <v-chip
        small
        :color="getTypeColor(document.type)"
        text-color="white"
        class="document-card__type-badge"
      >
        {{ getTypeName(document.type) }}
      </v-chip>
    </div>

    <!-- Document Info -->
    <v-card-text class="document-card__content">
      <h3 class="document-card__title" :title="document.title">
        {{ document.title }}
      </h3>
      
      <p v-if="document.description" class="document-card__description">
        {{ document.description }}
      </p>

      <div class="document-card__meta">
        <div class="document-card__meta-item">
          <v-icon small class="mr-1">storage</v-icon>
          <span>{{ formatFileSize(document.file_size) }}</span>
        </div>

        <div class="document-card__meta-item">
          <v-icon small class="mr-1">schedule</v-icon>
          <span>{{ formatDate(document.created_at) }}</span>
        </div>

        <v-btn
          v-if="document.downloadable"
          text
          x-small
          :color="actionButtonColor"
          @click.stop="downloadDocument"
          class="document-card__download-btn mt-2"
          :loading="downloading"
        >
          <v-icon left x-small>download</v-icon>
          Télécharger
        </v-btn>
      </div>
    </v-card-text>

    <!-- Actions -->
    <v-card-actions class="document-card__actions">
      <v-btn
        text
        small
        :color="actionButtonColor"
        @click.stop="previewDocument"
        class="document-card__action-btn"
        :loading="previewing"
      >
        <v-icon left small>{{ isIspring ? 'play_circle_outline' : 'visibility' }}</v-icon>
        {{ isIspring ? 'Consulter' : 'Visualiser' }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'DocumentCard',

  props: {
    document: {
      type: Object,
      required: true
    },
    color: {
      type: String,
      default: 'primary'
    }
  },

  data() {
    return {
      downloading: false,
      previewing: false
    }
  },

  computed: {
    ...mapGetters(['isThemeAja']),

    isIspring() {
      return this.document.format === 'ispring'
    },

    documentIcon() {
      return this.isIspring ? 'slideshow' : 'picture_as_pdf'
    },

    iconColor() {
      if (this.isIspring) {
        return 'orange'
      }
      return this.isThemeAja ? this.color : 'primary'
    },

    actionButtonColor() {
      if (this.isIspring) {
        return 'orange'
      }
      return this.isThemeAja ? this.color : 'primary'
    }
  },

  methods: {
    getTypeColor(type) {
      return this.isThemeAja ? '#4E983C' : '#FF9800'
    },

    getTypeName(type) {
      const types = {
        scientific_article: 'Article scientifique',
        vulgarization_article: 'Article vulgarisation',
        neuropsychological_test: 'Test neuropsychologique',
        oncogite_sheet: 'Fiche onCOGITE',
        various: 'Divers'
      }
      return types[type] || type
    },

    formatFileSize(bytes) {
      if (!bytes || isNaN(bytes)) return '-'
      const units = ['B', 'KB', 'MB', 'GB']
      let size = bytes
      let unitIndex = 0
      
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024
        unitIndex++
      }
      
      return size.toFixed(1) + ' ' + units[unitIndex]
    },

    formatDate(date) {
      return this.$moment(date).format('DD/MM/YYYY')
    },

    previewDocument() {
      this.previewing = true
      this.$emit('preview', this.document)
      // Reset previewing state after a short delay if not handled by parent
      setTimeout(() => {
        this.previewing = false
      }, 2000)
    },

    resetPreviewing() {
      this.previewing = false
    },

    async downloadDocument() {
      this.downloading = true
      try {
        await this.$emit('download', this.document)
      } finally {
        this.downloading = false
      }
    }
  }
}
</script>

<style scoped>
.document-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  cursor: pointer;
  border-radius: 12px;
  overflow: hidden;
}

.document-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

.document-card--aja {
  background: linear-gradient(135deg, #1E3945 0%, #294D5B 100%);
  color: white;
}

.document-card__header {
  position: relative;
  padding: 20px 20px 0;
  text-align: center;
}

.document-card__icon-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 80px;
  margin-bottom: 12px;
  margin-top: 12px;
}

.document-card__type-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.document-card__format-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  font-size: 0.65rem;
  font-weight: 600;
}

.document-card__content {
  flex: 1;
  padding: 0 20px 16px;
}

.document-card__title {
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.3;
  margin-bottom: 8px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.document-card__description {
  font-size: 0.9rem;
  line-height: 1.4;
  margin-bottom: 12px;
  opacity: 0.8;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.document-card__meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.document-card__meta-item {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  opacity: 0.7;
}

.document-card__actions {
  padding: 12px 12px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.document-card--aja .document-card__actions {
  border-top-color: rgba(255, 255, 255, 0.1);
}

.document-card__action-btn {
  transition: all 0.2s ease;
}

.document-card__download-btn {
  margin-top: 8px;
  margin-left: 0 !important;
  padding: 0 !important;
  min-width: auto !important;
  height: auto !important;
  justify-content: flex-start !important;
}

.document-card__action-btn:hover {
  transform: scale(1.02);
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .document-card__header {
    padding: 16px 16px 0;
  }
  
  .document-card__content {
    padding: 0 16px 12px;
  }
  
  .document-card__actions {
    padding: 8px 16px 16px;
  }
  
  .document-card__icon-wrapper {
    height: 60px;
  }
  
  .document-card__title {
    font-size: 1rem;
  }
}
</style> 